/** Relogio de Sao Paulo correspondente a `t` segundos depois de `from`. */
function clockAt(from: string, t: number): string {
	const [h, m] = from.split(':').map(Number)
	const total = h * 3600 + m * 60 + Math.floor(t)
	const hh = String(Math.floor(total / 3600) % 24).padStart(2, '0')
	const mm = String(Math.floor((total % 3600) / 60)).padStart(2, '0')
	const ss = String(total % 60).padStart(2, '0')
	return `${hh}:${mm}:${ss}`
}

const SPEEDS = [15, 30, 60, 120] as const

/** Espacamento das marcas da regua, em segundos de tempo simulado. */
const TICK_S = 1800

/**
 * O controle do tempo.
 *
 * E a unica peca da interface que ganha tratamento elaborado, e de proposito: o tempo e o
 * eixo deste instrumento inteiro - o dia que se escolhe, a janela de tres horas, o relogio
 * correndo. O trilho leva marca de meia em meia hora e o cursor e um TRACO vertical, que e
 * a forma de um marcador de horario; um disco de volume nao diz nada sobre horario.
 *
 * Duas linhas em vez de uma: com tudo numa fileira so, o trilho ficava espremido entre a
 * contagem e os botoes de velocidade, e arrastar tres horas em trezentos pixels e um
 * exercicio de pontaria. A regua ocupa a largura inteira; os comandos ficam embaixo.
 */
export function Timeline({
	from,
	durationS,
	time,
	playing,
	speed,
	running,
	convoys,
	moving,
	stopped,
	onToggle,
	onScrub,
	onSpeed,
}: {
	from: string
	durationS: number
	time: number
	playing: boolean
	speed: number
	/** Quantos onibus estao em viagem neste instante. */
	running: number
	/** Quantos onibus estao em comboio agora: alcancaram outro do mesmo sentido. */
	convoys: number
	/** Destes, quantos estao de fato andando e quantos estao parados. */
	moving: number
	stopped: number
	onToggle: () => void
	onScrub: (t: number) => void
	onSpeed: (s: number) => void
}) {
	// A marca acompanha a janela real: se a janela deixar de ter tres horas, o intervalo
	// entre marcas continua sendo meia hora em vez de virar uma fracao arbitraria.
	const tick = durationS > 0 ? `${(TICK_S / durationS) * 100}%` : '100%'

	return (
		<div className="rounded-lg border border-rule bg-card/95 px-4 pb-3 pt-3.5 shadow-sm backdrop-blur">
			<input
				type="range"
				min={0}
				max={durationS}
				step={1}
				value={Math.floor(time)}
				onChange={(e) => onScrub(Number(e.target.value))}
				aria-label="Linha do tempo"
				className="regua block h-4 w-full"
				style={{ '--tick': tick } as React.CSSProperties}
			/>
			<div className="mt-2 flex items-center gap-4">
				<button
					type="button"
					onClick={onToggle}
					aria-label={playing ? 'Pausar' : 'Reproduzir'}
					className="grid h-8 w-8 shrink-0 place-items-center rounded bg-ink text-[11px] text-card"
				>
					<span aria-hidden>{playing ? '❚❚' : '▶'}</span>
				</button>

				<span className="shrink-0 font-mono text-xl font-medium leading-none tracking-tight tabular-nums">
					{clockAt(from, time)}
				</span>

				<p className="min-w-0 flex-1 truncate font-display text-xs uppercase tracking-wide text-soft">
					<Count n={running} />
					<span className="text-ink"> em viagem</span>
					<Sep />
					<Count n={moving} /> andando
					<Sep />
					<Count n={stopped} /> parados
					{convoys > 0 && (
						<>
							<Sep />
							<span title="ônibus que alcançaram outro do mesmo sentido, a menos de 250 m">
								<Count n={convoys} /> em comboio
							</span>
						</>
					)}
				</p>

				<fieldset className="flex shrink-0 gap-0.5 border-0 p-0">
					<legend className="sr-only">Velocidade</legend>
					{SPEEDS.map((s) => (
						<button
							key={s}
							type="button"
							onClick={() => onSpeed(s)}
							aria-pressed={s === speed}
							className={`rounded px-1.5 py-1 font-mono text-[11px] leading-none ${
								s === speed ? 'bg-ink text-card' : 'text-soft hover:text-ink'
							}`}
						>
							{s}
							<span className="opacity-60">x</span>
						</button>
					))}
				</fieldset>
			</div>
		</div>
	)
}

function Count({ n }: { n: number }) {
	return <span className="font-mono text-sm tabular-nums text-ink">{n}</span>
}

function Sep() {
	return <span className="px-1.5 opacity-40">·</span>
}
